import React, { useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { MaskedLines, FadeIn } from "../motion/Reveal";

const scrollTo = (href) => {
  const el = document.querySelector(href);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
  else el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.1, 1.25]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative min-h-screen w-full overflow-hidden bg-[#222222]"
      data-testid="hero-section"
    >
      {/* Parallax background */}
      <motion.div
        aria-hidden
        style={{ y: bgY, scale: bgScale }}
        className="absolute inset-0"
      >
        <img
          src="https://images.pexels.com/photos/31570745/pexels-photo-31570745.jpeg"
          alt=""
          className="w-full h-[130%] object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 hero-gradient" />
        {/* subtle grid */}
        <div
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(250,250,250,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(250,250,250,0.5) 1px, transparent 1px)",
            backgroundSize: "80px 80px",
          }}
        />
      </motion.div>

      {/* Content */}
      <motion.div
        style={{ y: contentY }}
        className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 pt-40 pb-24 min-h-screen flex flex-col justify-between"
      >
        {/* Top eyebrow */}
        <FadeIn delay={0.35} className="flex items-center gap-4">
          <div className="h-[1px] w-16 bg-[#A4161A]" />
          <div className="font-btn text-[11px] tracking-[0.35em] uppercase text-[#FAFAFA]/80">
            Since 2000 · Enterprise IT · PAN India
          </div>
        </FadeIn>

        {/* Headline */}
        <div className="mt-16 lg:mt-24">
          <h1 className="sr-only">Empowering Businesses with Enterprise IT Solutions.</h1>
          <MaskedLines
            aria-hidden="true"
            className="font-display text-[#FAFAFA] text-[44px] sm:text-[64px] lg:text-[92px] leading-[0.98] tracking-[-0.03em] max-w-[16ch]"
            lines={[
              "Empowering",
              <>
                Businesses <span className="text-[#A4161A]">with</span>
              </>,
              "Enterprise IT",
              "Solutions.",
            ]}
            delayStart={0.5}
            stagger={0.14}
          />

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.35, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10 text-[#FAFAFA]/70 font-body text-[15px] lg:text-[17px] max-w-[54ch] leading-relaxed"
            data-testid="hero-subhead"
          >
            For 25+ years, DB Computronics has architected mission-critical IT infrastructure for
            corporates, manufacturers, hospitals, banks, and government institutions across India —
            with genuine products, corporate pricing, and engineering that shows up.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.55, ease: [0.16, 1, 0.3, 1] }}
            className="mt-12 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => scrollTo("#contact")}
              className="btn-red inline-flex items-center gap-3 px-7 py-4 rounded-lg font-btn text-[12px] tracking-[0.22em] uppercase"
              data-testid="hero-quote-btn"
            >
              <span>Request a Quote</span>
              <ArrowRight size={16} className="relative z-10" />
            </button>
            <button
              onClick={() => scrollTo("#contact")}
              className="btn-outline inline-flex items-center gap-3 px-7 py-4 rounded-lg font-btn text-[12px] tracking-[0.22em] uppercase"
              data-testid="hero-contact-btn"
            >
              <span>Contact Sales</span>
            </button>
          </motion.div>
        </div>

        {/* Bottom row: stats + scroll indicator */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.75, ease: [0.16, 1, 0.3, 1] }}
          className="mt-20 lg:mt-32 grid grid-cols-2 md:grid-cols-4 gap-8 border-t border-[#FAFAFA]/10 pt-8"
        >
          {[
            ["25+", "Years in Business"],
            ["10K+", "Endpoints Deployed"],
            ["220+", "Enterprise Clients"],
            ["100%", "Genuine Products"],
          ].map(([k, v], i) => (
            <div key={i} data-testid={`hero-stat-${i}`}>
              <div className="font-display text-[#FAFAFA] text-[32px] lg:text-[40px] leading-none tracking-tight">
                {k}
              </div>
              <div className="mt-2 text-[11px] tracking-[0.2em] uppercase text-[#9A9A9A]">{v}</div>
            </div>
          ))}
        </motion.div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.4, delay: 2 }}
        className="absolute bottom-8 right-8 z-10 hidden md:flex items-center gap-3 text-[#FAFAFA]/60"
      >
        <span className="text-[10px] tracking-[0.3em] uppercase font-btn">Scroll</span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        >
          <ArrowDown size={14} />
        </motion.div>
      </motion.div>
    </section>
  );
}
